import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Autocomplete,
  Typography,
  Stack,
  Snackbar,
  Alert,
  Grid2,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Iconify } from '../Minimal/src/components/iconify';
import { TablePaginationCustom } from '../Minimal/src/components/pagination';
import { useTable } from '../Minimal/src/hooks/use-table';
import Header from '../Header';
import Footer from '../Footer';
import CommonDatePicker from '../datepicker/CommonDatePicker';
import { format } from 'date-fns';
import config from "../common/Config";
import AccountService from '../../services/AccountServices';
import '../common/commonStyles.css';

const ProductLadger = () => {
  const accessToken = localStorage.getItem('accessToken');
  const [dates, setDates] = useState([null, null]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);

  const [resStartDate, setResStartDate] = useState('');
  const [resEndDate, setResEndDate] = useState('');

  const [productOptions, setProductOptions] = useState([]);
  const [productValue, setProductValue] = useState(null);
  const [flashMessage, setFlashMessage] = useState(null);

  const [productLedger, setProductLedger] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const table = useTable({
    defaultRowsPerPage: 50,
    defaultDense: false,
    defaultOrder: 'desc',
    defaultOrderBy: 'date',
    defaultSelected: [],
    defaultCurrentPage: 0,
  });

  const loadProductList = async () => {
    try {
      const data = await AccountService.fetchProductList(accessToken);
      const formattedOptions = data.products.map(item => ({
        label: `${item.name}  (${item.code}) (${item.size})`,
        value: item.id,
        code: item.code,
        name: item.name
      }));
      setProductOptions(formattedOptions);
    } catch (error) {
      setFlashMessage({ message: 'Failed to load product list', type: 'error' });
    }
  };

  const fetchProductLedger = async () => {
    setLoading(true);
    try {
      let startDate = dates[0] ? format(dates[0], 'dd/MM/yyyy') : '';
      let endDate = dates[1] ? format(dates[1], 'dd/MM/yyyy') : '';
      let dateRange = startDate ? startDate + ' - ' + endDate : '';

      const formData = new FormData();
      formData.append('draw', table.page + 1);
      formData.append('start', table.page * table.rowsPerPage);
      formData.append('length', table.rowsPerPage);
      formData.append('searchValue', searchQuery);
      formData.append('product_id', productValue ? productValue.value : '');
      formData.append('reportrange', dateRange);

      const response = await fetch(`${config.API_URL}/get_product_ladger_show`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      const json = await response.json();

      if (json) {
        setResStartDate(json.start_date || '');
        setResEndDate(json.end_date || '');
        const ledger = Array.isArray(json.data?.product_ledger) ? json.data.product_ledger : [];
        setProductLedger(ledger);
        setTotalCount(json.recordsTotal || json.recordsFiltered || ledger.length);
      } else {
        setProductLedger([]);
        setTotalCount(0);
      }
    } catch (error) {
      setFlashMessage({ message: 'Failed to load product ledger', type: 'error' });
      setProductLedger([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const searchFormData = () => {
    table.onChangePage(null, 0);
    fetchProductLedger();
  };

  const handleCloseMessage = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setFlashMessage(null);
  };

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }

    loadProductList();

    document.title = 'Product Ledger';
    return () => {
      document.title = 'Ebusso';
    };
  }, []);

  useEffect(() => {
    if (dates[0] && dates[1]) {
      fetchProductLedger();
    }
  }, [table.page, table.rowsPerPage]);

  const renderRow = (item, index) => {
    const {
      date,
      code,
      name,
      order_type,
      actual_box_balance,
      actual_piece_balance,
      sin,
      sout,
      id,
    } = item;

    const openingBalance = order_type === 'box'
      ? actual_box_balance
      : order_type === 'piece'
        ? actual_piece_balance
        : actual_box_balance;

    const soutNum = Number(sout) || 0;
    const sinNum = Number(sin) || 0;

    let balance = '';
    if (order_type === 'box') {
      const actualBoxBalance = Number(actual_box_balance) || 0;
      balance = (actualBoxBalance + soutNum) - sinNum;
    } else if (order_type === 'piece') {
      const actualPieceBalance = Number(actual_piece_balance) || 0;
      balance = (actualPieceBalance + soutNum) - sinNum;
    }

    const codeLink = (
      <a
        href={`${config.BASE_URL}/payments/view_product_ledger/${id}/${resStartDate}/${resEndDate}/${order_type}`}
        style={{ color: "#212b36", textDecoration: "underline" }}
      >
        {code}
      </a>
    );

    return (
      <TableRow
        key={`${id}-${index}-${date}`}
        className="table-cell-style"
      >
        <TableCell>{date || ''}</TableCell>
        <TableCell sx={{ textDecoration: 'underline !important',}}>{codeLink}</TableCell>
        <TableCell>{name || ''}</TableCell>
        <TableCell>{order_type || ''}</TableCell>
        <TableCell>{openingBalance || ''}</TableCell>
        <TableCell>{sin || ''}</TableCell>
        <TableCell>{sout || ''}</TableCell>
        <TableCell>{balance !== '' ? balance : ''}</TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1.2 }}>
            <Grid2 container spacing={1} justifyContent="space-between" alignItems="center">
              <Grid2 size={{ xs: 12, md: 2 }}>
                <Typography className="page-title">Product Ledger</Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <TextField
                  className="input-border"
                  placeholder="Search..."
                  value={searchQuery}
                  size="small"
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-outline" width={20} height={20} sx={{ color: "#637381" }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSearchQuery('');
                            }}
                            edge="end"
                          >
                            <Iconify icon="eva:close-fill" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{
                    width: "100%",
                    maxWidth: "400px",
                   
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <Autocomplete
                  options={productOptions}
                  value={productValue}
                  onChange={(event, newValue) => {
                    setProductValue(newValue);
                  }}
                  getOptionLabel={(option) => option.label || ''}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Product"
                      size="small"
                      className="input-border"
                      
                    />
                  )}
              
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Stack direction="row" spacing={1} flexDirection={{xs: 'column', md: 'row'}} alignItems="center">
                  <Box sx={{ width: {xs: '100%', md: '350px'} }}>
                    <CommonDatePicker className="input-border" value={dates} onChange={setDates} />
                  </Box>
                  
                  <Button
                    variant="contained"
                    className="button-variant-contained"
                    onClick={searchFormData}
                    sx={{ width: {xs: '100%', md: 'auto'} }}
                    
                  >
                    Submit
                  </Button>
                </Stack>
              </Grid2>
            </Grid2>
          </Box>
          <TableContainer
          >
            <Table
              size="medium"
              className="table-cell-border"
            > 
              <TableHead>
                <TableRow className="table-cell-header">
                  <TableCell width="150px">
                    Date
                  </TableCell>
                  <TableCell width="100px">
                    Code
                  </TableCell>
                  <TableCell width="250px">
                    Product
                  </TableCell>
                  <TableCell width="150px">
                    Type
                  </TableCell>
                  <TableCell width="150px">
                    O/B
                  </TableCell>
                  <TableCell width="150px">
                    In
                  </TableCell>
                  <TableCell width="150px">
                    Out
                  </TableCell>
                  <TableCell width="150px">
                    Bal
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : productLedger.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#0e0f0fef",
                          fontSize: "0.75rem",
                          lineHeight: 1.5,
                        }}
                      >
                        No product ledger data available.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  productLedger.map((item, index) => renderRow(item, index))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="flex-end" alignItems="center" pt={2}>
            <TablePaginationCustom
              className="pagination-styles"
              page={table.page}
              dense={table.dense}
              count={totalCount}
              rowsPerPage={table.rowsPerPage}
              onPageChange={table.onChangePage}
              onRowsPerPageChange={table.onChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={flashMessage !== null}
        autoHideDuration={6000}
        onClose={handleCloseMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseMessage}
          severity={flashMessage?.type === 'error' ? 'error' : 'success'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {flashMessage?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProductLadger;
